import axiosInstance from './axiosInstance';
import { getProductImageUrl } from './imageUtils';

export { getProductImageUrl };

// Helper to normalize list responses that may be wrapped
const normalizeList = (data, key) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[key])) return data[key];
  if (data && Array.isArray(data.data)) return data.data;
  return [];
};

const buildProductImageUrl = getProductImageUrl;

// Map backend product shape → frontend product card shape
const mapProductFromApi = (p) => {
  if (!p) return null;

  const firstImage =
    Array.isArray(p.images) && p.images.length > 0 ? p.images[0] : null;

  const imageUrl = firstImage
    ? buildProductImageUrl(firstImage.image_path || firstImage.path)
    : undefined;

  const images = Array.isArray(p.images) ? p.images : [];
  return {
    id: p.id,
    name: p.name,
    description: p.description || '',
    category: p.category_name,
    categoryId: p.category_id,
    price: Number(p.selling_price),
    castingPrice: Number(p.costing_price),
    stock: p.stock_quantity,
    stockUnit: p.weight_unit,
    weight: Number(p.weight),
    expiryDate: p.expiry_date ? p.expiry_date.slice(0, 10) : '',
    batchNumber: p.batch_number || '',
    imageUrl,
    images,
    supplierId: p.vendor_id ?? p.supplier_id ?? undefined,
    supplierName: p.vendor_name ?? p.supplier_name ?? '',
  };
};

const mapProductFromForm = (product, productId) =>
  mapProductFromApi({
    id: productId,
    name: product.name,
    description: product.description || '',
    category_id: product.categoryId,
    selling_price: product.price,
    costing_price: product.castingPrice,
    stock_quantity: product.stock,
    weight_unit: product.stockUnit || product.weightUnit || 'kg',
    weight: product.weight,
    expiry_date: product.expiryDate || null,
    batch_number: product.batchNumber || null,
    images: [],
  });

const fetchProductByIdSafe = async (productId, fallbackProduct) => {
  try {
    const response = await axiosInstance.get(`/products/${productId}`);
    return mapProductFromApi(response.data);
  } catch {
    return mapProductFromForm(fallbackProduct, productId);
  }
};

// Map frontend product form data → backend payload for create/update
const buildProductPayload = (product) => {
  const sellingPrice = Number(product.price);
  const costingPrice = Number(product.castingPrice);
  const weight = Number(product.weight);
  const stockQuantity =
    product.stock != null && product.stock !== ''
      ? Number(product.stock)
      : undefined;

  return {
    name: product.name,
    categoryId: product.categoryId ? Number(product.categoryId) : undefined,
    vendorId: product.supplierId ? Number(product.supplierId) : undefined,
    sellingPrice: Number.isNaN(sellingPrice) ? undefined : sellingPrice,
    costingPrice: Number.isNaN(costingPrice) ? undefined : costingPrice,
    weight: Number.isNaN(weight) ? undefined : weight,
    weightUnit: product.stockUnit || product.weightUnit || 'kg',
    stockQuantity: Number.isNaN(stockQuantity) ? undefined : stockQuantity,
    expiryDate: product.expiryDate || undefined,
    batchNumber: product.batchNumber || undefined,
    description: product.description || '',
  };
};

// ────────────────────────────────────────────────────────────────────────────────
// Categories
// ────────────────────────────────────────────────────────────────────────────────

const mapCategoryFromApi = (c) => {
  if (!c) return null;
  const name = c.name || c.title || '';

  // Ensure every category has a stable, non-empty id so that
  // the <select> in the ProductModal can distinguish options.
  const id =
    c.id ??
    c._id ??
    c.categoryId ??
    name; // fall back to name when there is no explicit id

  return {
    id,
    name,
    _raw: c,
  };
};

export const fetchCategories = async () => {
  const response = await axiosInstance.get('/products/categories');
  const list = normalizeList(response.data, 'categories');
  return list.map(mapCategoryFromApi).filter(Boolean);
};

export const createCategory = async ({ name }) => {
  const response = await axiosInstance.post('/products/categories', { name });
  const created = response.data?.category || response.data;
  // Backend might only return { message, categoryId }, so we attach the name we just submitted
  return mapCategoryFromApi({ ...created, name });
};

// ────────────────────────────────────────────────────────────────────────────────
// Products
// ────────────────────────────────────────────────────────────────────────────────

export const fetchProducts = async () => {
  const response = await axiosInstance.get('/products');
  const list = normalizeList(response.data, 'products');
  return list.map(mapProductFromApi).filter(Boolean);
};

export const createProduct = async (product) => {
  const payload = buildProductPayload(product);
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value);
    }
  });

  (product.images || [])
    .filter((file) => file instanceof File)
    .slice(0, 5)
    .forEach((file) => {
      formData.append('productImages', file);
    });

  const response = await axiosInstance.post('/products', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const productId = response.data?.productId ?? response.data?.id;
  if (productId) {
    return fetchProductByIdSafe(productId, product);
  }

  return mapProductFromForm(product);
};

export const updateProduct = async (id, product) => {
  const payload = buildProductPayload(product);
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value);
    }
  });

  // Backend: max 5 images total; only send new files, cap at 5
  (product.images || [])
    .filter((file) => file instanceof File)
    .slice(0, 5)
    .forEach((file) => {
      formData.append('newImages', file);
    });

  await axiosInstance.put(`/products/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return fetchProductByIdSafe(id, product);
};

export const deleteProduct = async (id) => {
  await axiosInstance.delete(`/products/${id}`);
};

// ────────────────────────────────────────────────────────────────────────────────
// Stock & Reporting (available for future use in UI)
// ────────────────────────────────────────────────────────────────────────────────

export const fetchLowStockProducts = async () => {
  const response = await axiosInstance.get('/products/low-stock');
  const list = normalizeList(response.data, 'products');
  return list.map(mapProductFromApi).filter(Boolean);
};

export const updateProductStock = async (id, { quantity, movementType, notes }) => {
  const response = await axiosInstance.post(`/products/${id}/stock`, {
    quantity,
    movementType,
    notes,
  });
  return response.data;
};

export const fetchStockMovements = async (id) => {
  const response = await axiosInstance.get(`/products/${id}/stock-movements`);
  return normalizeList(response.data, 'movements');
};

export const deleteProductImage = async (productId, imageId) => {
  const response = await axiosInstance.delete(`/products/${productId}/images/${imageId}`);
  return response.data;
};

export const fetchPriceHistory = async (id) => {
  const response = await axiosInstance.get(`/products/${id}/price-history`);
  return normalizeList(response.data, 'history');
};

export { mapProductFromApi };

